/**
 * PayPal Payment Buttons Component
 * Renders PayPal and Venmo payment buttons
 * Based on the PAYMENT document implementation plan
 */

import React, { useState, useEffect } from 'react';
import {
  PayPalScriptProvider,
  PayPalButtons,
  usePayPalScriptReducer,
  FUNDING,
} from '@paypal/react-paypal-js';
import {
  createPayPalOrder,
  capturePayPalOrder,
  subscribeToCheckoutStatus,
} from '../services/paymentService';
import { CheckoutStatus } from '../types/payment';
import { Alert, AlertDescription } from './ui/alert';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';

interface PayPalPaymentButtonsProps {
  checkoutId: string;
  clientId: string;
  paymentMethod?: 'paypal' | 'venmo'; // Which button to show
  onSuccess?: (checkoutId: string) => void;
  onError?: (error: Error) => void;
  onCancel?: () => void;
}

/**
 * Individual PayPal Button Component
 * Handles the payment flow for a specific funding source
 */
interface PayPalButtonWrapperProps {
  checkoutId: string;
  fundingSource: typeof FUNDING.PAYPAL | typeof FUNDING.VENMO;
  onSuccess?: (checkoutId: string) => void;
  onError?: (error: Error) => void;
  onCancel?: () => void;
}

function PayPalButtonWrapper({
  checkoutId,
  fundingSource,
  onSuccess,
  onError,
  onCancel,
}: PayPalButtonWrapperProps) {
  const [{ isPending }] = usePayPalScriptReducer();
  const [processing, setProcessing] = useState(false);
  const [status, setStatus] = useState<CheckoutStatus>('CREATED');

  // Log component mount
  useEffect(() => {
    console.log('🎯 PayPalButtonWrapper mounted', {
      checkoutId,
      fundingSource,
      timestamp: new Date().toISOString()
    });
    
    return () => {
      console.log('🎯 PayPalButtonWrapper unmounted', { checkoutId });
    };
  }, [checkoutId, fundingSource]);

  // Subscribe to checkout status updates
  useEffect(() => {
    console.log('📡 Setting up checkout status listener', { checkoutId });
    const unsubscribe = subscribeToCheckoutStatus(
      checkoutId,
      (update) => {
        console.log('📊 Checkout status update received', {
          checkoutId,
          oldStatus: status,
          newStatus: update.status,
          timestamp: new Date().toISOString()
        });
        setStatus(update.status);
        
        if (update.status === 'PAID') {
          console.log('✅ Payment confirmed via webhook!', { checkoutId });
          
          // Clear any pending timeout since webhook fired
          if ((window as any).__paypalWebhookTimeout) {
            console.log('🔄 Clearing webhook timeout - real webhook received');
            clearTimeout((window as any).__paypalWebhookTimeout);
            (window as any).__paypalWebhookTimeout = null;
          }
          
          if (onSuccess) {
            console.log('🎉 Calling onSuccess callback');
            onSuccess(checkoutId);
          }
          setProcessing(false);
        } else if (update.status === 'FAILED') {
          console.error('❌ Payment failed', {
            checkoutId,
            errorMessage: update.errorMessage
          });
          if (onError) {
            onError(new Error(update.errorMessage || 'Payment failed'));
          }
          setProcessing(false);
        }
      },
      (error) => {
        console.error('❌ Error listening to checkout status:', {
          checkoutId,
          error: error.message,
          code: (error as any).code,
          stack: error.stack
        });
        if (onError) {
          onError(error);
        }
        setProcessing(false);
      }
    );

    return () => {
      console.log('🔌 Unsubscribing from checkout status listener', { checkoutId });
      
      // Clear timeout on unmount
      if ((window as any).__paypalWebhookTimeout) {
        clearTimeout((window as any).__paypalWebhookTimeout);
        (window as any).__paypalWebhookTimeout = null;
      }
      
      unsubscribe();
    };
  }, [checkoutId, status, onSuccess, onError]);

  if (isPending) {
    console.log('⏳ PayPal SDK is loading...', { checkoutId });
    return (
      <div className="flex items-center justify-center py-4">
        <div className="flex items-center space-x-3">
          <div className="h-6 w-6 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin flex-shrink-0"></div>
          <span className="text-sm text-gray-600">Loading payment options...</span>
        </div>
      </div>
    );
  }

  console.log('✅ PayPal SDK loaded, rendering buttons', { 
    checkoutId, 
    fundingSource,
    processing 
  });

  return (
    <div className="relative">
      {processing && (
        <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="h-6 w-6 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin flex-shrink-0"></div>
            <span className="text-sm font-medium text-gray-700">Processing payment...</span>
          </div>
        </div>
      )}
      
      <PayPalButtons
        fundingSource={fundingSource}
        style={{
          layout: 'vertical',
          color: fundingSource === FUNDING.VENMO ? 'blue' : 'gold',
          shape: 'rect',
          label: 'pay',
          height: 48,
        }}
        createOrder={async () => {
          try {
            console.log('🔄 createOrder callback triggered', {
              checkoutId,
              fundingSource,
              timestamp: new Date().toISOString()
            });
            setProcessing(true);
            console.log('📝 Creating PayPal order for checkout:', checkoutId);
            
            const response = await createPayPalOrder(checkoutId);
            
            console.log('✅ PayPal order created successfully', {
              checkoutId,
              orderId: response.orderId,
              timestamp: new Date().toISOString()
            });
            return response.orderId;
          } catch (error: any) {
            console.error('❌ Error in createOrder callback', {
              checkoutId,
              error: error.message,
              code: (error as any).code,
              details: error,
              stack: error.stack
            });
            setProcessing(false);
            if (onError) {
              onError(error as Error);
            }
            throw error;
          }
        }}
        onApprove={async (data) => {
          try {
            console.log('✅ onApprove callback triggered - Payment approved by user', {
              checkoutId,
              orderId: data.orderID,
              payerId: data.payerID,
              facilitatorAccessToken: data.facilitatorAccessToken ? 'present' : 'missing',
              timestamp: new Date().toISOString()
            });
            console.log('🔄 Capturing PayPal order:', data.orderID);
            
            const response = await capturePayPalOrder(checkoutId, data.orderID);
            
            console.log('✅ Order captured successfully', {
              checkoutId,
              orderId: data.orderID,
              captureId: response.captureId,
              captureStatus: response.captureStatus,
              timestamp: new Date().toISOString()
            });

            // Check if capture was completed
            if (response.captureStatus === 'COMPLETED') {
              console.log('💡 Capture completed successfully');
              
              // Check if running in development mode (localhost)
              const isDevelopment = window.location.hostname === 'localhost' || 
                                    window.location.hostname === '127.0.0.1';
              
              if (isDevelopment) {
                // DEVELOPMENT MODE: Use timeout fallback since webhooks can't reach localhost
                console.log('🏠 Development mode detected - using webhook timeout fallback');
                console.log('⏳ Waiting for webhook confirmation (3 second timeout for local testing)...');
                
                const webhookTimeout = setTimeout(() => {
                  console.warn('⚠️ Webhook timeout - treating capture as success for local testing');
                  console.log('🎉 Proceeding with booking creation (local testing mode)');
                  
                  // Manually trigger success if still in CAPTURED state
                  if (onSuccess) {
                    onSuccess(checkoutId);
                  }
                  setProcessing(false);
                }, 3000); // 3 second timeout

                // Store timeout ID to clear it if webhook fires
                (window as any).__paypalWebhookTimeout = webhookTimeout;
              } else {
                // PRODUCTION MODE: Wait for webhook only (no timeout)
                console.log('🌐 Production mode detected - waiting for webhook only (no timeout)');
                console.log('⏳ Waiting for PayPal webhook to confirm payment...');
                console.log('💡 Webhook should fire within 1-2 seconds and mark status as PAID');
                // Keep processing state - will be cleared when webhook fires or on error
              }
            } else {
              console.warn('⚠️ Capture status is not COMPLETED', {
                captureStatus: response.captureStatus
              });
              setProcessing(false);
            }
            
            return;
          } catch (error: any) {
            console.error('❌ Error in onApprove callback', {
              checkoutId,
              orderId: data.orderID,
              error: error.message,
              code: (error as any).code,
              details: error,
              stack: error.stack
            });
            setProcessing(false);
            if (onError) {
              onError(error as Error);
            }
            throw error;
          }
        }}
        onCancel={() => {
          console.log('🚫 Payment canceled by user', {
            checkoutId,
            timestamp: new Date().toISOString()
          });
          setProcessing(false);
          if (onCancel) {
            onCancel();
          }
        }}
        onError={(err) => {
          console.error('❌ PayPal button error', {
            checkoutId,
            error: err,
            errorString: String(err),
            timestamp: new Date().toISOString()
          });
          setProcessing(false);
          if (onError) {
            onError(new Error(`Payment processing error: ${String(err)}`));
          }
        }}
      />
    </div>
  );
}

/**
 * Main PayPal Payment Buttons Component
 * Renders both PayPal and Venmo buttons with PayPal SDK provider
 */
export function PayPalPaymentButtons({
  checkoutId,
  clientId,
  paymentMethod = 'paypal',
  onSuccess,
  onError,
  onCancel,
}: PayPalPaymentButtonsProps) {
  const [status, setStatus] = useState<CheckoutStatus>('CREATED');

  // Log component initialization
  useEffect(() => {
    console.log('🎯 PayPalPaymentButtons component mounted', {
      checkoutId,
      clientId: clientId ? `${clientId.substring(0, 10)}...` : 'MISSING',
      paymentMethod,
      timestamp: new Date().toISOString()
    });

    if (!clientId) {
      console.error('❌ CRITICAL: PayPal clientId is missing!');
    }
  }, []);

  // Subscribe to checkout status for UI updates
  useEffect(() => {
    console.log('📡 PayPalPaymentButtons: Setting up status listener', { checkoutId });
    const unsubscribe = subscribeToCheckoutStatus(
      checkoutId,
      (update) => {
        console.log('📊 PayPalPaymentButtons: Status update', {
          checkoutId,
          newStatus: update.status,
          timestamp: new Date().toISOString()
        });
        setStatus(update.status);
      }
    );

    return () => {
      console.log('🔌 PayPalPaymentButtons: Unsubscribing from status listener');
      unsubscribe();
    };
  }, [checkoutId]);

  // Show success state
  if (status === 'PAID') {
    return (
      <Alert className="border-green-200 bg-green-50">
        <CheckCircle2 className="h-5 w-5 text-green-600" />
        <AlertDescription className="text-green-800 font-medium">
          Payment confirmed! Your booking is complete.
        </AlertDescription>
      </Alert>
    );
  }

  // Show failed state
  if (status === 'FAILED' || status === 'CANCELED') {
    return (
      <Alert className="border-red-200 bg-red-50">
        <XCircle className="h-5 w-5 text-red-600" />
        <AlertDescription className="text-red-800">
          Payment {status === 'FAILED' ? 'failed' : 'was canceled'}. Please try again.
        </AlertDescription>
      </Alert>
    );
  }

  console.log('🔧 Initializing PayPalScriptProvider', {
    checkoutId,
    clientId: clientId ? `${clientId.substring(0, 10)}...` : 'MISSING',
    paymentMethod,
    currentStatus: status
  });

  return (
    <PayPalScriptProvider
      options={{
        clientId: clientId,
        currency: 'USD',
        intent: 'capture',
        // Enable Venmo funding source
        enableFunding: 'venmo',
        // Disable other funding sources if needed
        // disableFunding: 'credit,card',
      }}
    >
      <div className="space-y-4">
        {/* Status indicator */}
        {(status === 'ORDER_CREATED' || status === 'CAPTURED') && (
          <Alert className="border-blue-200 bg-blue-50">
            <div className="flex items-center space-x-3">
              <div className="h-5 w-5 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin flex-shrink-0"></div>
              <AlertDescription className="text-blue-800">
                {status === 'ORDER_CREATED' && 'Processing payment...'}
                {status === 'CAPTURED' && 'Confirming payment...'}
              </AlertDescription>
            </div>
          </Alert>
        )}

        {/* Single Payment Button - PayPal or Venmo based on selection */}
        <div>
          <PayPalButtonWrapper
            checkoutId={checkoutId}
            fundingSource={paymentMethod === 'venmo' ? FUNDING.VENMO : FUNDING.PAYPAL}
            onSuccess={onSuccess}
            onError={onError}
            onCancel={onCancel}
          />
        </div>

        {/* Help text */}
        <p className="text-xs text-gray-500 text-center mt-4">
          Secure payment powered by PayPal. Your payment information is encrypted and secure.
        </p>
      </div>
    </PayPalScriptProvider>
  );
}

export default PayPalPaymentButtons;
